import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { format, addDays, parseISO } from "date-fns";
import {
  Plane,
  PlaneTakeoff,
  PlaneLanding,
  Filter,
  ChevronDown,
  Armchair,
  Luggage,
  BadgeCheck,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import FlightSearchWidget from "@/components/FlightSearchWidget";
import { airlineLogo, formatINR } from "@/mock";
import { apiSearchFlights } from "@/api";
import { apiFareCalendar } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { HotelCrossSell } from "@/components/CrossSell";
import { Slider } from "@/components/ui/slider";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { toast } from "sonner";
import { QuoteBar, QuoteCheck, MAX_QUOTE } from "@/components/FlightQuote";
import { Info, Clock, Briefcase, RefreshCcw, Tag } from "lucide-react";
import { format as fmtDate, parseISO as parseIsoD } from "date-fns";

const fkey = (f) => `${f?.flightNo || ""}@${f?.depTime || ""}`;
const pinFirst = (list, pin) => {
  if (!pin) return list;
  const hit = list.filter((f) => fkey(f) === pin);
  return [...hit, ...list.filter((f) => fkey(f) !== pin)];
};
const fmtDur = (m) => `${Math.floor((m || 0) / 60)}h ${String((m || 0) % 60).padStart(2, "0")}m`;
const segTime = (iso) => { try { return fmtDate(parseIsoD(iso), "HH:mm"); } catch { return (iso || "").slice(11, 16); } };
const segDate = (iso) => { try { return fmtDate(parseIsoD(iso), "dd MMM, EEE"); } catch { return ""; } };

const AIRLINE_NAMES = {
  "6E": "IndiGo",
  AI: "Air India",
  SG: "SpiceJet",
  UK: "Vistara",
  QP: "Akasa Air",
  EK: "Emirates",
};

const TIME_SLOTS = [
  { id: "morning", label: "Before 11 AM", test: (h) => h < 11 },
  { id: "midday", label: "11 AM - 4 PM", test: (h) => h >= 11 && h < 16 },
  { id: "evening", label: "4 PM - 9 PM", test: (h) => h >= 16 && h < 21 },
  { id: "night", label: "After 9 PM", test: (h) => h >= 21 || h < 5 },
];

// Same buckets, tested against arrival hour (cheapticket.in arrival-time filter)
const ARRIVAL_SLOTS = TIME_SLOTS;

// Rotating promo coupons shown per flight card (cheapticket.in style promo strip)
const CARD_COUPONS = [
  { code: "FLYTRIP300", off: "₹300", text: "Get Flat ₹300 OFF" },
  { code: "TRAVEL500", off: "₹500", text: "Save up to ₹500" },
  { code: "FIRSTFLY250", off: "₹250", text: "Extra ₹250 OFF for new users" },
  { code: "WEEKEND400", off: "₹400", text: "Weekend Special ₹400 OFF" },
];

// ---------- Inline "Flight Details" panel (segments / layover / fare summary / cancellation) ----------
const FlightDetailsPanel = ({ f }) => {
  const [tab, setTab] = useState("details");
  const segs = f.segments || [];
  const TABS = [
    { id: "details", label: "Flight Details" },
    { id: "fare", label: "Fare Summary" },
    { id: "cancel", label: "Cancellation / Date Change" },
  ];
  return (
    <div className="border-t border-slate-100 bg-slate-50/70 p-4" data-testid={`flight-details-panel-${f.id}`}>
      <div className="mb-3 flex gap-1.5 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`fd-tab-${t.id}-${f.id}`}
            onClick={() => setTab(t.id)}
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
              tab === t.id ? "bg-[#1B1F5B] text-white" : "bg-white text-slate-500 hover:text-[#1B1F5B]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "details" && (
        <div className="space-y-3">
          {segs.length === 0 && <p className="text-xs text-slate-400">Segment details not available for this fare.</p>}
          {segs.map((s, i) => (
            <div key={s.id || i}>
              <div className="rounded-xl border border-slate-100 bg-white p-3">
                <div className="flex items-center gap-2">
                  <img src={airlineLogo(s.code, 40)} alt="" className="h-6 w-6 rounded object-contain" />
                  <span className="text-xs font-bold text-slate-700">{s.airline} <span className="font-normal text-slate-400">{s.flightNo}</span></span>
                </div>
                <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                  <div>
                    <p className="text-base font-extrabold text-slate-800">{segTime(s.dep)}</p>
                    <p className="text-[11px] font-semibold text-slate-500">{s.from} ({s.fromCode})</p>
                    <p className="text-[10px] text-slate-400">{segDate(s.dep)}{s.fromTerminal ? ` · Term ${s.fromTerminal}` : ""}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] font-semibold text-slate-400">{fmtDur(s.duration)}</p>
                    <div className="my-1 flex items-center gap-1"><span className="h-px flex-1 bg-slate-200" /><Plane className="h-3 w-3 text-[#F5B32B]" /><span className="h-px flex-1 bg-slate-200" /></div>
                    <p className="text-[10px] text-slate-400">Non-stop</p>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-extrabold text-slate-800">{segTime(s.arr)}</p>
                    <p className="text-[11px] font-semibold text-slate-500">{s.to} ({s.toCode})</p>
                    <p className="text-[10px] text-slate-400">{segDate(s.arr)}{s.toTerminal ? ` · Term ${s.toTerminal}` : ""}</p>
                  </div>
                </div>
              </div>
              {s.layoverMins > 0 && (
                <div className="my-1.5 flex items-center justify-center gap-1.5 rounded-lg bg-amber-50 py-1.5 text-[11px] font-bold text-amber-700">
                  <Clock className="h-3 w-3" /> Layover at {s.to} · {fmtDur(s.layoverMins)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "fare" && (
        <div className="rounded-xl border border-slate-100 bg-white p-4">
          <div className="flex items-center justify-between text-sm text-slate-600"><span>Total fare (per adult)</span><span className="font-extrabold text-[#1B1F5B]">{formatINR(f.price)}</span></div>
          <p className="mt-1 text-[11px] text-slate-400">Inclusive of base fare, airline taxes &amp; fees. Exact break-up shown at review.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600"><Briefcase className="h-3 w-3" /> Check-in {f.fareOptions?.[0]?.checkInBaggage || "15 KG"}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600"><Luggage className="h-3 w-3" /> Cabin {f.fareOptions?.[0]?.cabinBaggage || "7 KG"}</span>
            {f.refundable && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-600"><BadgeCheck className="h-3 w-3" /> Refundable</span>}
          </div>
        </div>
      )}

      {tab === "cancel" && (
        <div className="rounded-xl border border-slate-100 bg-white p-4 text-[12px] leading-relaxed text-slate-600">
          <p className="flex items-center gap-1.5 font-bold text-slate-700"><RefreshCcw className="h-3.5 w-3.5 text-[#1B1F5B]" /> Cancellation &amp; Date Change</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>Cancellation &amp; date-change charges apply as per airline policy + convenience fee.</li>
            <li>Charges depend on how close to departure you cancel; partial refunds are processed to source.</li>
            <li>{f.refundable ? "This fare is refundable (airline penalty may apply)." : "This is a non-refundable fare — only statutory taxes may be refundable."}</li>
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------- 7-day date + lowest-fare strip (cheapticket.in style) ----------
const DateFareStrip = ({ from, to, dep, onPick }) => {
  const today = useMemo(() => new Date(new Date().setHours(0, 0, 0, 0)), []);
  const [anchor, setAnchor] = useState(() => parseISO(dep));
  const [fares, setFares] = useState({});

  useEffect(() => { setAnchor(parseISO(dep)); }, [dep]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)), [anchor]);

  useEffect(() => {
    if (!from || !to || from === to) return undefined;
    const months = [...new Set(days.map((d) => format(d, "yyyy-MM")))];
    let active = true;
    Promise.all(months.map((m) => apiFareCalendar(from, to, m).catch(() => null))).then((rs) => {
      if (!active) return;
      const merged = {};
      rs.forEach((r) => { if (r?.fares) Object.assign(merged, r.fares); });
      setFares((prev) => ({ ...prev, ...merged }));
    });
    return () => { active = false; };
  }, [from, to, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  const lowest = useMemo(() => {
    const vals = days.map((d) => fares[format(d, "yyyy-MM-dd")]).filter((v) => v != null);
    return vals.length ? Math.min(...vals) : null;
  }, [days, fares]);

  const shift = (n) => setAnchor((a) => { const nx = addDays(a, n); return nx < today ? today : nx; });

  return (
    <div className="mb-4 flex items-center gap-2" data-testid="date-fare-strip">
      <button
        type="button"
        aria-label="Previous dates"
        onClick={() => shift(-7)}
        disabled={anchor <= today}
        className="flex h-11 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-[#1B1F5B] hover:text-[#1B1F5B] disabled:opacity-30"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="no-scrollbar flex flex-1 gap-1.5 overflow-x-auto">
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const isSel = key === dep;
          const fare = fares[key];
          const isLow = lowest != null && fare === lowest;
          return (
            <button
              key={key}
              type="button"
              data-testid={`date-fare-${key}`}
              onClick={() => !isSel && onPick(key)}
              className={`flex min-w-[88px] flex-1 shrink-0 flex-col items-center rounded-xl border px-2 py-2 text-center transition-all ${
                isSel
                  ? "border-[#1B1F5B] bg-[#1B1F5B] text-white shadow-md"
                  : "border-slate-200 bg-white text-slate-700 hover:border-[#1B1F5B]/50 hover:shadow-sm"
              }`}
            >
              <span className={`text-[11px] font-semibold ${isSel ? "text-white/80" : "text-slate-400"}`}>
                {format(d, "EEE, dd MMM")}
              </span>
              <span className={`mt-0.5 text-[13px] font-extrabold ${isSel ? "text-white" : isLow ? "text-emerald-600" : "text-[#1B1F5B]"}`}>
                {fare != null ? `₹${fare >= 1000 ? `${(fare / 1000).toFixed(1)}k` : fare}` : "—"}
              </span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        aria-label="Next dates"
        onClick={() => shift(7)}
        className="flex h-11 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-[#1B1F5B] hover:text-[#1B1F5B]"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const FlightResults = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add: addToCart } = useCart();

  const hasSearchParams = !!(params.get("from") && params.get("to"));
  const from = params.get("from") || "DEL";
  const to = params.get("to") || "BOM";
  const fromCity = params.get("fromCity") || "Delhi";
  const toCity = params.get("toCity") || "Mumbai";
  const dep = params.get("dep") || format(new Date(Date.now() + 864e5), "yyyy-MM-dd");
  const ret = params.get("ret") || "";
  const adults = Number(params.get("ADT") || 1);
  const children = Number(params.get("CHD") || 0);
  const infants = Number(params.get("INF") || 0);
  const pax = adults + children + infants;
  const cabin = params.get("cabin") || "Economy";

  const [allFlights, setAllFlights] = useState([]);
  const [returnFlights, setReturnFlights] = useState([]);
  const [combos, setCombos] = useState([]);
  const [selOnward, setSelOnward] = useState(null);
  const [selReturn, setSelReturn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("mock");
  const [providerError, setProviderError] = useState("");

  useEffect(() => {
    // No route params (e.g. footer/direct link) → send user to homepage search
    // instead of silently showing the default Delhi→Mumbai route.
    if (!hasSearchParams) navigate("/", { replace: true });
  }, [hasSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasSearchParams) return undefined;
    let active = true;
    setLoading(true);
    setProviderError("");
    setSelOnward(null);
    setSelReturn(null);
    apiSearchFlights({
      fromCode: from,
      toCode: to,
      date: dep,
      adults,
      children,
      infants,
      cabin,
      ...(ret ? { returnDate: ret } : {}),
    })
      .then((res) => {
        if (!active) return;
        setAllFlights(res.flights || []);
        setReturnFlights(res.returnFlights || []);
        setCombos(res.combos || []);
        setSource(res.source || "mock");
        setProviderError(res.provider_error || "");
      })
      .catch(() => {
        if (!active) return;
        setSource("error");
        setProviderError("Network error. Please retry.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [from, to, dep, ret, adults, children, infants, cabin]);

  // Backend already applies cabin pricing; use price as-is.
  const priceFor = (f) => f.price;
  const isAgent = user?.role === "agent";
  const [quote, setQuote] = useState([]);
  const inQuote = (id) => quote.some((x) => x.id === id);
  const toggleQuote = (item) => setQuote((q) => {
    if (q.some((x) => x.id === item.id)) return q.filter((x) => x.id !== item.id);
    if (q.length >= MAX_QUOTE) { toast.error(`Maximum ${MAX_QUOTE} options in one quotation`); return q; }
    return [...q, item];
  });
  const owItem = (f) => ({ id: f.id, legs: [{ ...f, from, to, date: dep }], fare: f.price, legFares: null });
  const pairItem = (a, b) => ({ id: `${a.id}|${b.id}`, legs: [{ ...a, from, to, date: dep }, { ...b, from: to, to: from, date: ret }], fare: a.price + b.price, legFares: [a.price, b.price] });
  const comboItem = (cb) => ({ id: cb.id, legs: [{ ...cb.onward, from, to, date: dep }, { ...cb.return, from: to, to: from, date: ret }], fare: cb.price, legFares: null });

  const [sort, setSort] = useState("cheapest");
  const [stopFilter, setStopFilter] = useState([]);
  const [airlineFilter, setAirlineFilter] = useState([]);
  const [slotFilter, setSlotFilter] = useState([]);
  const [arrSlotFilter, setArrSlotFilter] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [fareModal, setFareModal] = useState(null);

  const FILTER_STORE = "ft_flight_filters";
  const skipSave = React.useRef(true);
  // Load last-used filters once on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FILTER_STORE) || "{}");
      if (Array.isArray(saved.stopFilter)) setStopFilter(saved.stopFilter);
      if (Array.isArray(saved.slotFilter)) setSlotFilter(saved.slotFilter);
      if (typeof saved.sort === "string") setSort(saved.sort);
    } catch { /* ignore */ }
  }, []);
  const maxPrice = useMemo(
    () => (allFlights.length ? Math.max(...allFlights.map((f) => f.price)) : 100000),
    [allFlights]
  );
  const minPrice = useMemo(
    () => (allFlights.length ? Math.min(...allFlights.map((f) => f.price)) : 0),
    [allFlights]
  );
  const [priceCap, setPriceCap] = useState(null);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rtApi, setRtApi] = useState(null);
  const [rtSlide, setRtSlide] = useState(0);

  useEffect(() => {
    if (!rtApi) return undefined;
    const onSelect = () => setRtSlide(rtApi.selectedScrollSnap());
    onSelect();
    rtApi.on("select", onSelect);
    rtApi.on("reInit", onSelect);
    return () => {
      rtApi.off("select", onSelect);
      rtApi.off("reInit", onSelect);
    };
  }, [rtApi]);

  const goRtSlide = (index) => rtApi?.scrollTo(index);

  const toggle = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);

  // Persist route-agnostic filters whenever they change (Save Filters).
  // Skip the very first run so the mount-time empty state never overwrites
  // the values the load effect is about to restore.
  useEffect(() => {
    if (skipSave.current) { skipSave.current = false; return; }
    try {
      localStorage.setItem(FILTER_STORE, JSON.stringify({ stopFilter, slotFilter, sort }));
    } catch { /* ignore */ }
  }, [stopFilter, slotFilter, sort]);

  const flights = useMemo(() => {
    let list = [...allFlights];
    if (stopFilter.length)
      list = list.filter((f) => stopFilter.includes(f.stops));
    if (airlineFilter.length)
      list = list.filter((f) => airlineFilter.includes(f.code));
    if (slotFilter.length)
      list = list.filter((f) =>
        slotFilter.some((id) => TIME_SLOTS.find((s) => s.id === id)?.test(f.depHour))
      );
    if (arrSlotFilter.length)
      list = list.filter((f) =>
        arrSlotFilter.some((id) => ARRIVAL_SLOTS.find((s) => s.id === id)?.test(f.arrHour))
      );
    if (priceCap) list = list.filter((f) => f.price <= priceCap);
    if (sort === "cheapest") list.sort((a, b) => a.price - b.price);
    if (sort === "fastest") list.sort((a, b) => a.durationMins - b.durationMins);
    if (sort === "nonstop") list.sort((a, b) => a.stops - b.stops || a.price - b.price);
    if (sort === "best")
      list.sort(
        (a, b) => a.price + a.durationMins * 8 - (b.price + b.durationMins * 8)
      );
    return pinFirst(list, params.get("pin"));
  }, [allFlights, stopFilter, airlineFilter, slotFilter, arrSlotFilter, priceCap, sort, params]);
  const retList = useMemo(() => pinFirst(returnFlights, params.get("pinr")), [returnFlights, params]);
  const comboList = useMemo(() => {
    const p = params.get("pin"), r = params.get("pinr");
    if (!p) return combos;
    const hit = combos.filter((c) => fkey(c.onward) === p && (!r || fkey(c.return) === r));
    return [...hit, ...combos.filter((c) => !hit.includes(c))];
  }, [combos, params]);

  const activeFilterCount =
    stopFilter.length + airlineFilter.length + slotFilter.length + arrSlotFilter.length +
    (priceCap && priceCap < maxPrice ? 1 : 0);

  // Counts per filter option (from the full result set, cheapticket.in style)
  const cStop = (v) => allFlights.filter((f) => f.stops === v).length;
  const cDep = (id) => allFlights.filter((f) => TIME_SLOTS.find((s) => s.id === id)?.test(f.depHour)).length;
  const cArr = (id) => allFlights.filter((f) => ARRIVAL_SLOTS.find((s) => s.id === id)?.test(f.arrHour)).length;
  const cAir = (c) => allFlights.filter((f) => f.code === c).length;

  // Shared filter body - rendered in the desktop sidebar AND the mobile panel
  const FilterBody = ({ p }) => (
    <>
      <div className="mt-4 border-t pt-4">
        <p className="mb-2 text-[13px] font-bold text-slate-700">Stops</p>
        {[
          { v: 0, l: "Non-stop" },
          { v: 1, l: "1 Stop" },
        ].map((s) => (
          <label key={s.v} data-testid={`${p}-filter-stop-${s.v}`} className="flex cursor-pointer items-center gap-2 py-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={stopFilter.includes(s.v)}
              onChange={() => toggle(stopFilter, setStopFilter, s.v)}
              className="h-4 w-4 accent-[#1B1F5B]"
            />
            <span className="flex-1">{s.l}</span>
            <span className="text-xs font-semibold text-slate-400">({cStop(s.v)})</span>
          </label>
        ))}
      </div>

      <div className="mt-4 border-t pt-4">
        <p className="mb-2 text-[13px] font-bold text-slate-700">Departure Time</p>
        <div className="grid grid-cols-2 gap-1.5">
          {TIME_SLOTS.map((s) => (
            <button
              key={s.id}
              data-testid={`${p}-filter-slot-${s.id}`}
              onClick={() => toggle(slotFilter, setSlotFilter, s.id)}
              className={`rounded-lg border px-1.5 py-1.5 text-[11px] font-semibold transition-colors ${
                slotFilter.includes(s.id)
                  ? "border-[#1B1F5B] bg-[#1B1F5B] text-white"
                  : "border-slate-200 text-slate-600 hover:border-[#1B1F5B]/40"
              }`}
            >
              {s.label} <span className="opacity-70">({cDep(s.id)})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 border-t pt-4">
        <p className="mb-2 text-[13px] font-bold text-slate-700">Arrival Time</p>
        <div className="grid grid-cols-2 gap-1.5">
          {ARRIVAL_SLOTS.map((s) => (
            <button
              key={s.id}
              data-testid={`${p}-filter-arrslot-${s.id}`}
              onClick={() => toggle(arrSlotFilter, setArrSlotFilter, s.id)}
              className={`rounded-lg border px-1.5 py-1.5 text-[11px] font-semibold transition-colors ${
                arrSlotFilter.includes(s.id)
                  ? "border-[#1B1F5B] bg-[#1B1F5B] text-white"
                  : "border-slate-200 text-slate-600 hover:border-[#1B1F5B]/40"
              }`}
            >
              {s.label} <span className="opacity-70">({cArr(s.id)})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 border-t pt-4" data-testid={`${p}-filter-max-price`}>
        <p className="mb-1 text-[13px] font-bold text-slate-700">Max Price</p>
        <Slider
          value={[priceCap || maxPrice]}
          min={minPrice}
          max={maxPrice}
          step={100}
          onValueChange={([v]) => setPriceCap(v)}
        />
        <p className="mt-1 text-xs font-semibold text-[#1B1F5B]">
          Up to {formatINR(priceCap || maxPrice)}
        </p>
      </div>

      <div className="mt-4 border-t pt-4">
        <p className="mb-2 text-[13px] font-bold text-slate-700">Airlines</p>
        {airlinesPresent.map((c) => (
          <label key={c} data-testid={`${p}-filter-airline-${c}`} className="flex cursor-pointer items-center gap-2 py-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={airlineFilter.includes(c)}
              onChange={() => toggle(airlineFilter, setAirlineFilter, c)}
              className="h-4 w-4 accent-[#1B1F5B]"
            />
            <img src={airlineLogo(c, 40)} alt="" className="h-5 w-5 object-contain" />
            <span className="flex-1">{AIRLINE_NAMES[c] || c}</span>
            <span className="text-xs font-semibold text-slate-400">({cAir(c)})</span>
          </label>
        ))}
      </div>
    </>
  );

  const pickDate = (newDep) => {
    const p = new URLSearchParams(params);
    p.set("dep", newDep);
    navigate(`/flight/search?${p.toString()}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goCheckout = (flight, fare = null) => {
    const chosen = fare ? { ...flight, priceId: fare.priceId, price: fare.price } : flight;
    if (!chosen.priceId && source === "tripjack") {
      toast.error("This fare is no longer available. Please retry search.");
      return;
    }
    sessionStorage.setItem(
      "ft_checkout",
      JSON.stringify({ flight: chosen, fromCode: from, toCode: to, fromCity, toCity, date: dep, cabin, pax, adults, children, infants })
    );
    navigate("/checkout");
  };

  const goCheckoutRt = () => {
    if (!selOnward || !selReturn) return;
    if ((!selOnward.priceId || !selReturn.priceId) && source === "tripjack") {
      toast.error("Selected fare is no longer available. Please reselect.");
      return;
    }
    sessionStorage.setItem(
      "ft_checkout",
      JSON.stringify({ roundtrip: true, legs: [selOnward, selReturn], flight: selOnward, fromCode: from, toCode: to, fromCity, toCity, date: dep, retDate: ret, cabin, pax, adults, children, infants })
    );
    navigate("/checkout");
  };

  // International round-trip: COMBO = married onward+return with ONE combined priceId
  const isCombo = !!ret && combos.length > 0;

  const goCheckoutCombo = (combo, fare = null) => {
    const priceId = fare ? fare.priceId : combo.priceId;
    const price = fare ? fare.price : combo.price;
    if (!priceId && source === "tripjack") {
      toast.error("This fare is no longer available. Please retry search.");
      return;
    }
    const legOW = { id: combo.id + "-ow", ...combo.onward, priceId, price, fromCode: from, toCode: to };
    const legRT = { id: combo.id + "-rt", ...combo.return, priceId, price, fromCode: to, toCode: from };
    sessionStorage.setItem(
      "ft_checkout",
      JSON.stringify({ roundtrip: true, combo: true, comboPriceId: priceId, legs: [legOW, legRT], flight: legOW, fromCode: from, toCode: to, fromCity, toCity, date: dep, retDate: ret, cabin, pax, adults, children, infants })
    );
    navigate("/checkout");
  };

  // Combo leg row (onward/return inside an international RT combo card)
  const ComboLeg = ({ leg, fromC, toC, dateLabel, accent }) => (
    <div className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${accent ? "bg-[#0E7C6B]/5" : "bg-slate-50"}`}>
      <span className={`w-14 shrink-0 text-[10px] font-extrabold uppercase ${accent ? "text-[#0E7C6B]" : "text-[#1B1F5B]"}`}>{fromC} → {toC}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-bold text-slate-800">{leg.airline} <span className="font-normal text-slate-400">{leg.flightNo}</span></p>
        <p className="text-[10px] text-slate-400">{dateLabel} · {leg.duration} · {leg.stops === 0 ? "Non-stop" : `${leg.stops} stop${leg.via ? ` via ${leg.via}` : ""}`}</p>
      </div>
      <div className="shrink-0 text-right text-sm font-extrabold text-slate-800">
        {leg.depTime} <span className="font-normal text-slate-300">→</span> {leg.arrTime}
      </div>
    </div>
  );

  // Compact selectable card for round-trip leg lists
  const RtLegCard = ({ f, selected, onSelect, testid, fromCode, toCode }) => (
    <div data-testid={testid}
      className={`rounded-2xl border p-4 shadow-sm transition-all ${selected ? "border-[#0E7C6B] bg-[#0E7C6B]/5 ring-1 ring-[#0E7C6B]" : "border-slate-100 bg-white hover:border-[#1B1F5B]/30"}`}>
      <div className="flex items-center gap-2">
        <img src={airlineLogo(f.code, 40)} alt="" className="h-7 w-7 rounded object-contain" />
        <p className="truncate text-sm font-bold text-slate-800">{f.airline} <span className="font-normal text-slate-400">{f.flightNo}</span></p>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-center">
          <p className="text-base font-extrabold text-slate-800">{f.depTime}</p>
          <p className="text-[10px] text-slate-400">{fromCode}</p>
        </div>
        <div className="flex-1 px-2 text-center">
          <p className="text-[10px] text-slate-400">{f.duration}</p>
          <div className="my-0.5 h-px bg-slate-200" />
          <p className="text-[10px] font-semibold text-slate-500">{f.stops === 0 ? "Non-stop" : `${f.stops} stop${f.via ? " · " + f.via : ""}`}</p>
        </div>
        <div className="text-center">
          <p className="text-base font-extrabold text-slate-800">{f.arrTime}{f.nextDay && <sup className="text-[#E89A10]">+1</sup>}</p>
          <p className="text-[10px] text-slate-400">{toCode}</p>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-dashed pt-2">
        <p className="text-lg font-extrabold text-[#1B1F5B]">{formatINR(f.price)}<span className="text-[10px] font-normal text-slate-400"> /adult</span></p>
        <button data-testid={`${testid}-select`} onClick={() => onSelect(selected ? null : f)}
          className={`rounded-lg px-4 py-1.5 text-xs font-extrabold transition-colors ${selected ? "bg-[#0E7C6B] text-white" : "bg-[#F5B32B] text-[#0A0F2E] hover:bg-[#E89A10]"}`}>
          {selected ? "Selected ✓" : "Select"}
        </button>
      </div>
    </div>
  );

  const airlinesPresent = [...new Set(allFlights.map((f) => f.code))];


  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar />
      <Header />

      {/* Summary bar */}
      <div className="sticky top-[68px] z-40 border-b border-slate-100 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <div className="flex items-center gap-2 text-lg font-extrabold text-slate-800">
              {fromCity} <span className="text-[#1B1F5B]">({from})</span>
              <Plane className="h-4 w-4 text-[#F5B32B]" />
              {toCity} <span className="text-[#1B1F5B]">({to})</span>
            </div>
            <p className="text-xs text-slate-400">
              {dep} | {pax} Traveller{pax > 1 ? "s" : ""} | {cabin} | {flights.length} flights found
            </p>
            {!loading && source === "tripjack" && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-600">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> Live fares by TripJack
              </span>
            )}
            {!loading && source === "mock" && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-600">
                Demo fares (live key not configured)
              </span>
            )}
          </div>
          <button
            onClick={() => setModifyOpen(!modifyOpen)}
            className="flex items-center gap-1.5 rounded-lg border border-[#1B1F5B] px-4 py-2 text-sm font-bold text-[#1B1F5B] transition-colors hover:bg-[#1B1F5B] hover:text-white"
          >
            Modify Search <ChevronDown className={`h-4 w-4 transition-transform ${modifyOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
        {modifyOpen && (
          <div className="mx-auto max-w-7xl px-4 pb-5">
            <FlightSearchWidget
              compact
              initial={{
                tripType: ret ? "round" : "one-way",
                from: { code: from, city: fromCity, name: "" },
                to: { code: to, city: toCity, name: "" },
                depart: dep ? new Date(`${dep}T00:00:00`) : undefined,
                ret: ret ? new Date(`${ret}T00:00:00`) : null,
                pax: { adults, children, infants },
                cabin,
              }}
              onSearched={(p) => {
                navigate(`/flight/search?${p.toString()}`);
                setModifyOpen(false);
                window.scrollTo(0, 0);
              }}
            />
          </div>
        )}
      </div>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[260px_1fr]">
        {/* Filters */}
        <aside className="h-fit rounded-2xl border border-slate-100 bg-white p-5 shadow-sm max-lg:hidden lg:sticky lg:top-[150px]">
          <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-800">
            <Filter className="h-4 w-4 text-[#1B1F5B]" /> Filters
          </h3>
          <FilterBody p="desktop" />
        </aside>

        {/* Results */}
        <div className="min-w-0">
          {/* Mobile filter trigger + MMT-style bottom sheet (sidebar hidden < lg) */}
          <div className="mb-3 lg:hidden">
            <button
              data-testid="mobile-filters-btn"
              onClick={() => setFiltersOpen(true)}
              className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-[#1B1F5B] shadow-sm active:scale-[0.99]"
            >
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <span data-testid="mobile-filters-count" className="rounded-full bg-[#1B1F5B] px-2 py-0.5 text-[10px] font-extrabold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </span>
              <span className="text-xs font-semibold text-slate-400">Tap to filter</span>
            </button>
          </div>

          <Drawer open={filtersOpen} onOpenChange={setFiltersOpen}>
            <DrawerContent data-testid="mobile-filters-panel" className="max-h-[85vh]">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 pb-3 pt-2">
                <DrawerTitle className="flex items-center gap-2 text-base font-extrabold text-[#1B1F5B]">
                  <Filter className="h-4 w-4" /> Filters
                </DrawerTitle>
                <button data-testid="mobile-filters-close" onClick={() => setFiltersOpen(false)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="overflow-y-auto px-5 pb-2">
                <FilterBody p="mobile" />
              </div>
              <div className="flex gap-3 border-t border-slate-100 p-4">
                <button
                  data-testid="mobile-filters-clear"
                  onClick={() => { setStopFilter([]); setAirlineFilter([]); setSlotFilter([]); setArrSlotFilter([]); setPriceCap(null); }}
                  className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600"
                >
                  Clear All
                </button>
                <button
                  data-testid="mobile-filters-apply"
                  onClick={() => setFiltersOpen(false)}
                  className="flex-[2] rounded-xl bg-[#1B1F5B] py-3 text-sm font-extrabold text-white"
                >
                  Show {flights.length} flight{flights.length !== 1 ? "s" : ""}
                </button>
              </div>
            </DrawerContent>
          </Drawer>

          {/* Date + lowest-fare strip (one-way, cheapticket.in style) */}
          {!ret && source !== "error" && (
            <DateFareStrip from={from} to={to} dep={dep} onPick={pickDate} />
          )}

          {/* Sort tabs */}
          <div className="mb-4 grid grid-cols-2 overflow-hidden rounded-xl border border-slate-100 bg-white text-center shadow-sm sm:grid-cols-4">
            {[
              { id: "cheapest", label: "Cheapest", sub: (() => { const top = [...flights].sort((a, b) => a.price - b.price)[0]; return top ? formatINR(top.price) : ""; })() },
              { id: "nonstop", label: "Non Stop First", sub: (() => { const top = [...flights].filter((f) => f.stops === 0).sort((a, b) => a.price - b.price)[0]; return top ? formatINR(top.price) : "No non-stop"; })() },
              { id: "fastest", label: "Fastest", sub: "Shortest duration" },
              { id: "best", label: "Recommended", sub: "Best value" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`sort-tab-${t.id}`}
                onClick={() => setSort(t.id)}
                className={`border-r border-slate-100 px-3 py-2.5 transition-colors last:border-r-0 ${
                  sort === t.id ? "bg-[#1B1F5B] text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <span className="block text-sm font-bold">{t.label}</span>
                <span className={`block text-[11px] ${sort === t.id ? "text-white/70" : "text-slate-400"}`}>
                  {t.sub}
                </span>
              </button>
            ))}
          </div>

          {loading && (
            <div className="rounded-2xl border border-slate-100 bg-white py-20 text-center shadow-sm">
              <Loader2 className="mx-auto h-9 w-9 animate-spin text-[#1B1F5B]" />
              <p className="mt-3 text-sm font-semibold text-slate-500">
                Searching best fares for you...
              </p>
            </div>
          )}

          {!loading && source === "error" && (
            <div className="rounded-2xl border border-red-100 bg-white py-16 text-center shadow-sm">
              <Plane className="mx-auto h-10 w-10 text-red-300" />
              <p className="mt-3 font-bold text-slate-700">Live fares temporarily unavailable</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">
                {providerError || "Could not fetch live fares right now. Please retry."}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 rounded-lg bg-[#1B1F5B] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#141844]"
              >
                Retry Search
              </button>
            </div>
          )}

          {!loading && source === "no_flights" && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
              <Plane className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-bold text-slate-700">No live flights found</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">
                No flights available for this route/date. Try a different date or nearby airport.
              </p>
              <button
                onClick={() => setModifyOpen(true)}
                className="mt-4 rounded-lg bg-[#1B1F5B] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#141844]"
              >
                Modify Search
              </button>
            </div>
          )}

          {!loading && source !== "error" && source !== "no_flights" && flights.length === 0 && combos.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
              <Plane className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-semibold text-slate-500">No flights match your filters</p>
              <button
                onClick={() => {
                  setStopFilter([]);
                  setAirlineFilter([]);
                  setSlotFilter([]);
                  setArrSlotFilter([]);
                  setPriceCap(null);
                }}
                className="mt-3 rounded-lg bg-[#1B1F5B] px-5 py-2 text-sm font-bold text-white"
              >
                Clear Filters
              </button>
            </div>
          )}

          {/* INTERNATIONAL ROUND TRIP: married combo cards (one combined fare) */}
          {!loading && isCombo && (
            <div data-testid="combo-view">
              <h2 className="mb-1 font-[Poppins] text-lg font-extrabold text-slate-800">Round-trip fares: {fromCity} ⇄ {toCity}</h2>
              <p className="mb-4 text-xs text-slate-400">{combos.length} international round-trip options · price shown is for BOTH flights together</p>
              <div className="space-y-3">
                {comboList.map((cb) => (
                  <div key={cb.id} data-testid={`combo-card-${cb.id}`}
                    className="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_4px_18px_rgba(27,31,91,0.06)] transition-all duration-300 hover:border-[#1B1F5B]/25 hover:shadow-[0_12px_32px_rgba(27,31,91,0.12)]">
                    <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[1fr_auto]">
                      <div className="space-y-2">
                        <ComboLeg leg={cb.onward} fromC={from} toC={to} dateLabel={dep} accent={false} />
                        <ComboLeg leg={cb.return} fromC={to} toC={from} dateLabel={ret} accent={true} />
                      </div>
                      <div className="flex items-center justify-between gap-3 md:flex-col md:items-end md:justify-center">
                        <div className="text-left md:text-right">
                          <p className="text-xl font-extrabold text-[#1B1F5B]">{formatINR(cb.price)}</p>
                          <p className="text-[10px] text-slate-400">round-trip / adult{cb.seatsLeft <= 3 ? ` · ${cb.seatsLeft} left` : ""}</p>
                        </div>
                        {isAgent && <QuoteCheck id={cb.id} checked={inQuote(cb.id)} onToggle={() => toggleQuote(comboItem(cb))} />}
                        <button
                          data-testid={`combo-select-${cb.id}`}
                          onClick={() => goCheckoutCombo(cb)}
                          className="rounded-xl bg-[#F5B32B] px-6 py-2.5 text-sm font-extrabold text-[#1B1F5B] transition-colors hover:bg-[#e0a020]"
                        >
                          Book
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ROUND TRIP: desktop keeps the MMT two-column view; mobile uses a finger-swipe slider */}
          {!loading && ret && returnFlights.length > 0 && !isCombo && (
            <div data-testid="roundtrip-view">
              <h2 className="mb-4 font-[Poppins] text-lg font-extrabold text-slate-800">Flights from {fromCity} to {toCity}, and back</h2>

              <div className="mb-4 lg:hidden" data-testid="rt-mobile-slider">
                <div className="mb-3 grid grid-cols-2 gap-2" role="tablist" aria-label="Round trip flight selection">
                  <button
                    type="button"
                    data-testid="rt-tab-onward"
                    role="tab"
                    aria-selected={rtSlide === 0}
                    onClick={() => goRtSlide(0)}
                    className={`rounded-2xl border p-3 text-left shadow-sm transition-colors ${rtSlide === 0 ? "border-[#1B1F5B] bg-white ring-1 ring-[#1B1F5B]" : "border-slate-200 bg-white/75"}`}
                  >
                    <span className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-[#1B1F5B]">
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${selOnward ? "bg-[#0E7C6B] text-white" : "bg-[#1B1F5B] text-white"}`}>1</span>
                      Onward
                    </span>
                    <span className="mt-1 block truncate text-sm font-bold text-slate-800">
                      {selOnward ? `${selOnward.airline} · ${selOnward.depTime}` : `${from} → ${to}`}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-slate-400">{selOnward ? formatINR(selOnward.price) : dep}</span>
                  </button>
                  <button
                    type="button"
                    data-testid="rt-tab-return"
                    role="tab"
                    aria-selected={rtSlide === 1}
                    onClick={() => goRtSlide(1)}
                    className={`rounded-2xl border p-3 text-left shadow-sm transition-colors ${rtSlide === 1 ? "border-[#0E7C6B] bg-white ring-1 ring-[#0E7C6B]" : "border-slate-200 bg-white/75"}`}
                  >
                    <span className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-[#0E7C6B]">
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${selReturn ? "bg-[#0E7C6B] text-white" : "bg-slate-300 text-white"}`}>2</span>
                      Return
                    </span>
                    <span className="mt-1 block truncate text-sm font-bold text-slate-800">
                      {selReturn ? `${selReturn.airline} · ${selReturn.depTime}` : `${to} → ${from}`}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-slate-400">{selReturn ? formatINR(selReturn.price) : ret}</span>
                  </button>
                </div>

                <Carousel
                  data-testid="rt-carousel"
                  setApi={setRtApi}
                  opts={{ align: "start", containScroll: "trimSnaps" }}
                  className="w-full min-w-0"
                >
                  <CarouselContent className="ml-0">
                    <CarouselItem className="pl-0" data-testid="rt-slide-onward">
                      <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-3">
                        <div className="flex items-center gap-2 rounded-xl bg-[#1B1F5B] px-4 py-2.5">
                          <span className="text-sm font-extrabold text-white">{from} → {to}</span>
                          <span className="ml-auto text-[11px] text-white/70">{dep}</span>
                        </div>
                        {selOnward ? (
                          <button
                            type="button"
                            data-testid="rt-mobile-choose-return"
                            onClick={() => goRtSlide(1)}
                            className="mt-3 flex w-full items-center justify-between rounded-xl bg-[#0E7C6B] px-4 py-3 text-left text-sm font-extrabold text-white shadow-sm transition-transform active:scale-[0.99]"
                          >
                            Onward selected · Choose return flight
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        ) : (
                          <p className="mt-3 rounded-xl bg-white px-3 py-2 text-center text-xs font-semibold text-slate-500">
                            Pehle onward flight select karein, phir swipe karke return chunein
                          </p>
                        )}
                        <div className="mt-3 space-y-3">
                          {flights.map((f) => (
                            <RtLegCard key={f.id} f={f} testid={`rt-m-onward-${f.id}`} selected={selOnward?.id === f.id} onSelect={setSelOnward} fromCode={from} toCode={to} />
                          ))}
                        </div>
                      </div>
                    </CarouselItem>

                    <CarouselItem className="pl-0" data-testid="rt-slide-return">
                      <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-3">
                        <div className="flex items-center gap-2 rounded-xl bg-[#0E7C6B] px-4 py-2.5">
                          <span className="text-sm font-extrabold text-white">{to} → {from}</span>
                          <span className="ml-auto text-[11px] text-white/70">{ret}</span>
                        </div>
                        <p className="mt-3 rounded-xl bg-white px-3 py-2 text-center text-xs font-semibold text-slate-500">
                          Return flight select karke combined fare neeche dekhein
                        </p>
                        <div className="mt-3 space-y-3">
                          {retList.map((f) => (
                            <RtLegCard key={f.id} f={f} testid={`rt-m-return-${f.id}`} selected={selReturn?.id === f.id} onSelect={setSelReturn} fromCode={to} toCode={from} />
                          ))}
                        </div>
                      </div>
                    </CarouselItem>
                  </CarouselContent>
                </Carousel>

                <div className="mt-3 flex items-center justify-center gap-1.5" data-testid="rt-slide-dots">
                  {[0, 1].map((i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`rt-dot-${i}`}
                      aria-label={i === 0 ? "Show onward flights" : "Show return flights"}
                      onClick={() => goRtSlide(i)}
                      className={`h-2 rounded-full transition-all ${rtSlide === i ? "w-7 bg-[#1B1F5B]" : "w-2 bg-slate-300"}`}
                    />
                  ))}
                </div>
              </div>

              <div className="hidden gap-5 lg:grid lg:grid-cols-2">
                <div>
                  <div className="mb-3 flex items-center gap-2 rounded-xl bg-[#1B1F5B] px-4 py-2.5">
                    <span className="text-sm font-extrabold text-white">{from} → {to}</span>
                    <span className="ml-auto text-[11px] text-white/70">{dep}</span>
                  </div>
                  <div className="space-y-3 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
                    {flights.map((f) => (
                      <RtLegCard key={f.id} f={f} testid={`rt-onward-${f.id}`} selected={selOnward?.id === f.id} onSelect={setSelOnward} fromCode={from} toCode={to} />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-3 flex items-center gap-2 rounded-xl bg-[#0E7C6B] px-4 py-2.5">
                    <span className="text-sm font-extrabold text-white">{to} → {from}</span>
                    <span className="ml-auto text-[11px] text-white/70">{ret}</span>
                  </div>
                  <div className="space-y-3 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
                    {retList.map((f) => (
                      <RtLegCard key={f.id} f={f} testid={`rt-return-${f.id}`} selected={selReturn?.id === f.id} onSelect={setSelReturn} fromCode={to} toCode={from} />
                    ))}
                  </div>
                </div>
              </div>
              <div className={quote.length ? "h-48 lg:h-40" : "h-28 lg:h-24"} />
            </div>
          )}

          {!loading && !(ret && (returnFlights.length > 0 || combos.length > 0)) && (
          <div className="space-y-3">
            {flights.map((f, idx) => {
              const coupon = CARD_COUPONS[idx % CARD_COUPONS.length];
              return (
              <div
                key={f.id}
                className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_18px_rgba(27,31,91,0.06)] transition-all duration-300 hover:border-[#1B1F5B]/25 hover:shadow-[0_12px_32px_rgba(27,31,91,0.12)]"
              >
              <div
                className="group grid grid-cols-1 items-center gap-4 p-4 md:grid-cols-[1.1fr_1.6fr_auto_auto]"
              >
                {/* Airline */}
                <div className="flex items-center gap-3">
                  <img
                    src={airlineLogo(f.code, 90)}
                    alt={f.airline}
                    className="h-10 w-10 rounded-full border border-slate-100 object-contain p-1"
                  />
                  <div>
                    <p className="text-sm font-bold text-slate-800">{f.airline}</p>
                    <p className="text-xs text-slate-400">{f.flightNo}</p>
                  </div>
                </div>

                {/* Times */}
                <div className="flex items-center gap-3">
                  <div>
                    <p className="flex items-center gap-1 text-lg font-extrabold text-slate-800">
                      <PlaneTakeoff className="h-4 w-4 text-slate-400" /> {f.depTime}
                    </p>
                    <p className="pl-5 text-xs font-semibold text-slate-400">{from}</p>
                  </div>
                  <div className="flex-1 text-center">
                    <p className="text-[11px] font-semibold text-slate-400">{f.duration}</p>
                    <div className="relative my-1 border-t-2 border-dashed border-slate-200">
                      <Plane className="absolute -top-[9px] left-1/2 h-4 w-4 -translate-x-1/2 text-[#F5B32B]" />
                    </div>
                    <p className={`text-[11px] font-bold ${f.stops === 0 ? "text-emerald-600" : "text-amber-600"}`}>
                      {f.stops === 0 ? "Non-stop" : `1 Stop via ${f.via}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="flex items-center gap-1 text-lg font-extrabold text-slate-800">
                      <PlaneLanding className="h-4 w-4 text-slate-400" /> {f.arrTime}
                    </p>
                    <p className="pl-5 text-xs font-semibold text-slate-400">{to}</p>
                  </div>
                </div>

                {/* Meta */}
                <div className="flex gap-3 text-[11px] text-slate-500 md:flex-col md:gap-1">
                  <span className="flex items-center gap-1">
                    <Armchair className="h-3.5 w-3.5 text-[#1B1F5B]" /> {cabin}
                  </span>
                  <span className="flex items-center gap-1">
                    <Luggage className="h-3.5 w-3.5 text-[#1B1F5B]" /> {f.fareOptions?.[0]?.checkInBaggage || "15 KG"}
                  </span>
                  {f.refundable && (
                    <span className="flex items-center gap-1 text-emerald-600">
                      <BadgeCheck className="h-3.5 w-3.5" /> Refundable
                    </span>
                  )}
                </div>

                {/* Price + CTA */}
                <div className="text-right">
                  <p className="text-xl font-extrabold text-[#1B1F5B]">
                    {formatINR(priceFor(f))}
                  </p>
                  <p className="text-[11px] font-semibold text-red-500">
                    Only {f.seatsLeft} seats left
                  </p>
                  {isAgent && <div className="mt-1 flex justify-end"><QuoteCheck id={f.id} checked={inQuote(f.id)} onToggle={() => toggleQuote(owItem(f))} /></div>}
                  {f.fareOptions && f.fareOptions.length > 1 ? (
                    <button
                      onClick={() => setFareModal(f)}
                      data-testid={`view-fares-btn-${f.id}`}
                      className="mt-2 rounded-lg bg-[#F5B32B] px-6 py-2 text-sm font-extrabold text-[#0A0F2E] shadow-sm transition-all duration-200 hover:bg-[#E89A10] hover:shadow-md"
                    >
                      View Fares ({f.fareOptions.length})
                    </button>
                  ) : (
                    <button
                      onClick={() => goCheckout(f)}
                      data-testid="book-now-btn"
                      className="mt-2 rounded-lg bg-[#F5B32B] px-6 py-2 text-sm font-extrabold text-[#0A0F2E] shadow-sm transition-all duration-200 hover:bg-[#E89A10] hover:shadow-md"
                    >
                      Book Now
                    </button>
                  )}
                  <button
                    onClick={() => addToCart({
                      kind: "flight",
                      title: `${f.airline} ${f.flightNo || ""} · ${from}-${to}`,
                      route: `${fromCity} -> ${toCity}`,
                      date: dep,
                      price: priceFor(f) * pax,
                      pax,
                      meta: { from, to, flightNo: f.flightNo, airline: f.airline },
                    })}
                    data-testid="add-to-cart-btn"
                    className="mt-1.5 block w-full rounded-lg border border-[#1B1F5B]/20 px-4 py-1.5 text-xs font-bold text-[#1B1F5B] transition-colors hover:bg-[#1B1F5B]/5"
                  >
                    + Add to Trip
                  </button>
                </div>
              </div>

              {/* Coupon promo strip (cheapticket.in style) */}
              <div className="flex items-center gap-2 border-t border-dashed border-emerald-200 bg-emerald-50/70 px-4 py-2" data-testid={`coupon-strip-${f.id}`}>
                <Tag className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <p className="text-[12px] font-semibold text-emerald-700">
                  {coupon.text}! Use code{" "}
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard?.writeText(coupon.code); toast.success(`Coupon ${coupon.code} copied!`); }}
                    className="rounded border border-dashed border-emerald-500 bg-white px-1.5 py-0.5 font-extrabold text-emerald-700 hover:bg-emerald-100"
                    data-testid={`coupon-code-${f.id}`}
                  >
                    {coupon.code}
                  </button>
                </p>
              </div>

              {/* Flight Details toggle */}
              <button
                type="button"
                data-testid={`flight-details-toggle-${f.id}`}
                onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 py-2 text-[12px] font-bold text-[#1B1F5B] transition-colors hover:bg-[#1B1F5B]/[0.03]"
              >
                <Info className="h-3.5 w-3.5" /> Flight Details
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedId === f.id ? "rotate-180" : ""}`} />
              </button>
              {expandedId === f.id && <FlightDetailsPanel f={f} />}
              </div>
              );
            })}
          </div>
          )}

          {!loading && allFlights.length > 0 && (
            <HotelCrossSell city={toCity} checkIn={dep} adults={adults} />
          )}
        </div>
      </div>

      {/* Round-trip combined selection bar */}
      {ret && returnFlights.length > 0 && (selOnward || selReturn) && (
        <div data-testid="rt-book-bar" className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] backdrop-blur">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm sm:flex sm:flex-wrap sm:items-center sm:gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase text-slate-400">Onward</p>
                <p className="truncate font-bold text-slate-700">{selOnward ? `${selOnward.airline} ${selOnward.depTime} · ${formatINR(selOnward.price)}` : "Select a departure flight"}</p>
              </div>
              <span className="text-slate-300">+</span>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase text-slate-400">Return</p>
                <p className="truncate font-bold text-slate-700">{selReturn ? `${selReturn.airline} ${selReturn.depTime} · ${formatINR(selReturn.price)}` : "Select a return flight"}</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 sm:justify-end sm:gap-4">
              <div className="text-left sm:text-right">
                <p className="text-[10px] font-bold uppercase text-slate-400">Total for {pax} traveller{pax > 1 ? "s" : ""}</p>
                <p data-testid="rt-total" className="text-xl font-extrabold text-[#1B1F5B]">{formatINR(((selOnward?.price || 0) + (selReturn?.price || 0)) * pax)}</p>
              </div>
              {isAgent && (
                <button data-testid="rt-add-quote-btn" disabled={!selOnward || !selReturn}
                  onClick={() => toggleQuote(pairItem(selOnward, selReturn))}
                  className="rounded-xl border-2 border-[#1B1F5B] px-3 py-2.5 text-xs font-extrabold text-[#1B1F5B] transition-colors hover:bg-[#1B1F5B]/5 disabled:opacity-40">
                  {selOnward && selReturn && inQuote(`${selOnward.id}|${selReturn.id}`) ? "Remove from quote" : "Add pair to quote"}
                </button>
              )}
              <button data-testid="rt-book-btn" disabled={!selOnward || !selReturn} onClick={goCheckoutRt}
                className="min-w-[150px] rounded-xl bg-[#F5B32B] px-4 py-3 text-sm font-extrabold text-[#0A0F2E] shadow transition-colors hover:bg-[#E89A10] disabled:opacity-40 sm:px-6">
                BOOK ROUND TRIP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fare options modal (branded fares — cheapticket.in "View Price" style) */}
      <Dialog open={!!fareModal} onOpenChange={(o) => !o && setFareModal(null)}>
        <DialogContent className="max-w-3xl" data-testid="fare-options-modal">
          {fareModal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
                  <img src={airlineLogo(fareModal.code, 40)} alt="" className="h-6 w-6 rounded object-contain" />
                  {fareModal.airline} <span className="font-normal text-slate-400">{fareModal.flightNo}</span>
                  <span className="ml-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">
                    {from} {fareModal.depTime} → {to} {fareModal.arrTime}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs font-bold uppercase tracking-wide text-[#1B1F5B]">
                {fareModal.fareOptions.length} Fare options available for your trip
              </p>
              <div className="grid max-h-[60vh] gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
                {fareModal.fareOptions.map((opt, i) => (
                  <div
                    key={opt.priceId || i}
                    data-testid={`fare-option-${i}`}
                    className={`flex flex-col rounded-2xl border p-4 shadow-sm transition-all ${
                      i === 0 ? "border-[#1B1F5B] ring-1 ring-[#1B1F5B]/30" : "border-slate-200 hover:border-[#1B1F5B]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="rounded-full bg-[#1B1F5B]/[0.06] px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-[#1B1F5B]">{opt.name}</span>
                      {i === 0 && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">Lowest</span>}
                    </div>
                    <p className="mt-2 text-xl font-extrabold text-[#1B1F5B]">{formatINR(opt.price)}</p>
                    <p className="text-[10px] text-slate-400">per adult</p>
                    <ul className="mt-3 flex-1 space-y-1.5 text-[12px] text-slate-600">
                      <li className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-[#1B1F5B]" /> Check-in: {opt.checkInBaggage}</li>
                      <li className="flex items-center gap-1.5"><Luggage className="h-3.5 w-3.5 text-[#1B1F5B]" /> Cabin: {opt.cabinBaggage}</li>
                      <li className={`flex items-center gap-1.5 ${opt.refundable ? "text-emerald-600" : "text-slate-400"}`}>
                        {opt.refundable ? <BadgeCheck className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        {opt.refundable ? "Refundable" : "Non-refundable"}
                      </li>
                    </ul>
                    <button
                      data-testid={`fare-book-${i}`}
                      onClick={() => { const flt = fareModal; setFareModal(null); goCheckout(flt, opt); }}
                      className={`mt-3 rounded-lg py-2 text-sm font-extrabold transition-colors ${
                        i === 0 ? "bg-[#F5B32B] text-[#0A0F2E] hover:bg-[#E89A10]" : "border border-[#1B1F5B] text-[#1B1F5B] hover:bg-[#1B1F5B] hover:text-white"
                      }`}
                    >
                      Book Now
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
      {isAgent && <QuoteBar items={quote} onClear={() => setQuote([])} raised={!!(ret && returnFlights.length > 0 && (selOnward || selReturn))} search={{ from, to, fromCity, toCity, date: dep, retDate: ret, pax, adults, children, infants, cabin }} />}
    </div>
  );
};

export default FlightResults;
